import { Dropdown } from 'primereact/dropdown';
import { Fieldset } from 'primereact/fieldset';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import deptService from './dept-service';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import BreadcrumbComponent from '../../../../components/Breadcrumb';


interface DropdownItem {
    fullDesc: string;
    referenceCode: string;
}

const currentDate = new Date().toLocaleString("en-US", { timeZone: "Asia/Kuala_Lumpur" });

const DeptEdit: React.FC = () => {

    const location = useLocation();
    const navigate = useNavigate();
    const deptData = location.state?.dept;
    const isEditMode = !!deptData;
    const toast = useRef<Toast>(null);

    const [loading, setLoading] = useState(false);
    const [clientState, setClientState] = useState<any>(null);
    const [formData, setFormData] = useState<any>({});
    const [errors, setErrors] = useState<any>({});

    const [stateItems, setStateItems] = useState<DropdownItem[]>([]);
    const [countryItems, setCountryItems] = useState<DropdownItem[]>([]);
    const [districtItems, setDistrictItems] = useState<DropdownItem[]>([]);
    const [postcodeItems, setPostcodeItems] = useState<DropdownItem[]>([]);
    const dropdownStateItems = useMemo(() => stateItems, [stateItems]);
    const dropdownCountryItems = useMemo(() => countryItems, [countryItems]);
    const dropdownDistrictItems = useMemo(() => districtItems, [districtItems]);
    const dropdownPostcodeItems = useMemo(() => postcodeItems, [postcodeItems]);

    useEffect(() => {
        const fetchClientState = async () => {
            try {
                const clientStateItem = await deptService.getClientState();
                setClientState(clientStateItem);

                const stateData = await deptService.getStateDropdown('MYS');
                setStateItems(stateData);

                const countryData = await deptService.getCountryDropdown();
                setCountryItems(countryData);


                if (isEditMode) {
                    setFormData(deptData)

                    if (deptData) {
                        const districtData = await deptService.getDistrictDropdown(deptData.stateCode);
                        setDistrictItems(districtData);

                        const postcodeData = await deptService.getPostcodeDropdown(deptData.stateCode);
                        setPostcodeItems(postcodeData);
                    }
                }

            } catch (error) {
                console.error('Error fetching client state:', error);
            }
        };

        fetchClientState();
    }, []);

    const validate = () => {
        let valid = true;

        if (!formData.departmentCode) {
            errors.departmentCode = 'Department is required';
            valid = false;
        }
        if (!formData.fullDesc) {
            errors.fullDesc = 'Full Description is required';
            valid = false;
        }
        if (!formData.departmentCategory) {
            errors.departmentCategory = 'Department Category is required';
            valid = false;
        }
        setFormData({ ...formData, errors });
        return valid;
    }

    const formatDate = (dateString: any) => {
        const date = new Date(dateString);
        return date.toLocaleString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hourCycle: "h23",
            timeZone: "Asia/Kuala_Lumpur",
        }).replace(", ", " ");
    };

    const handleInputChange = (e: any) => {
        const { id, value } = e.target;
        setFormData({
            ...formData,
            [id]: value.toUpperCase()
        });
    };

    const handleDropdownChange = (e: any) => {
        const { id, value } = e.target;
        setFormData({
            ...formData,
            [id]: value
        });
    };

    const handleStateDropdownChange = async (e: any) => {
        const stateCode = e.value

        if (stateCode) {
            try {
                const districtData = await deptService.getDistrictDropdown(stateCode);
                setDistrictItems(districtData);

                const postcodeData = await deptService.getPostcodeDropdown(stateCode);
                setPostcodeItems(postcodeData);
            } catch (error) {
                console.error('Error fetching district data:', error);
            }
        }

        setFormData({
            ...formData,
            stateCode: e.value
        });

    };

    const toolbarTemplate = () => {
        function handleBack(): void {
            navigate('/admin/department');
        }
        function handleSave(event: React.FormEvent): void {
            event.preventDefault();

            if (validate()) {

                setLoading(true);


                if (isEditMode) {
                    formData.modifiedBy = clientState.userId;
                    formData.modifiedDate = currentDate
                } else {
                    formData.insertBy = clientState.userId;
                    formData.insertDate = currentDate;
                    formData.modifiedBy = clientState.userId;
                    formData.modifiedDate = currentDate;
                    formData.facilityCode = clientState.facilityCode;
                }

                if (isEditMode) {
                    deptService.update(formData.facilityCode, formData.departmentCode, formData).then((response) => {
                        const data = response.data;
                        clientState.success.detail = 'Already Save Record';
                        toast.current?.show(clientState.success);
                        navigate("/admin/department");
                    }).catch((error) => {
                        console.error('Error updating module:', error);
                        toast.current?.show({ severity: 'error', summary: 'Update Module', detail: 'Error updating module' });
                    }).finally(() => {
                        setLoading(false);
                    });
                } else {
                    deptService.create(formData).then((response) => {
                        const data = response.data;
                        clientState.success.detail = 'Already Save Record';
                        toast.current?.show(clientState.success);
                        navigate("/admin/department");
                    });
                }
            }


        }

        return (
            <>
                <Toast ref={toast} />
                <Button label="Save" icon="pi pi-check" style={{ marginRight: '.5em' }} onClick={handleSave} disabled={loading}>

                </Button>
                {/* <Button label="Delete" icon="pi pi-trash" style={{ marginRight: '.5em' }} /> */}
                <Button label="Back" icon="pi pi-times" style={{ marginRight: '.5em' }} onClick={handleBack}>

                </Button>

            </>
        );
    };

    return (
        <div className='grid'>
            <div className='col-12'>
                <div className='card-title'>
                    <h5>Department Form</h5>
                    <BreadcrumbComponent />
                </div>
                <div className='form-title'>
                    <Fieldset legend="Module Information">
                        <div className="p-fluid form-grid grid">
                            <div className="field col-12 md:col-6">
                                <label>Department Code</label>
                                <InputText
                                    id="departmentCode"
                                    value={formData.departmentCode}
                                    onChange={handleInputChange}
                                />
                                {errors.departmentCode && <small className="p-error">{errors.departmentCode}</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Department Category</label>
                                <InputText
                                    id="departmentCategory"
                                    value={formData.departmentCategory}
                                    onChange={handleInputChange}
                                />
                                {errors.departmentCategory && <small className="p-error">{errors.departmentCategory}</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Full Description</label>
                                <InputText
                                    id="fullDesc"
                                    value={formData.fullDesc}
                                    onChange={handleInputChange}
                                />
                                {errors.fullDesc && <small className="p-error">{errors.fullDesc}</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Short Description</label>
                                <InputText
                                    id="shortDesc"
                                    value={formData.shortDesc}
                                    onChange={handleInputChange}
                                />
                                {errors.shortDesc && <small className="p-error">{errors.shortDesc}</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <h6>Status</h6>
                                <InputSwitch
                                    checked={formData.recordStatus === "1"}
                                    onChange={(e) =>
                                        setFormData({ ...formData, recordStatus: e.value ? "1" : "0" })
                                    }
                                />
                            </div>
                            <div className="field col-12">
                                <label>Address</label>
                                <InputText
                                    id="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Country</label>
                                <Dropdown
                                    id="countryCode"
                                    value={formData.countryCode}
                                    onChange={handleDropdownChange}
                                    options={dropdownCountryItems}
                                    optionLabel="fullDesc"
                                    placeholder="Select One"
                                    optionValue="countryCode"
                                >
                                </Dropdown>
                                {errors.countryCode && <small className="p-error">{errors.countryCode}</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>State</label>
                                <Dropdown
                                    id="stateCode"
                                    value={formData.stateCode}
                                    onChange={(e) => handleStateDropdownChange(e)}
                                    options={dropdownStateItems}
                                    optionLabel="fullDesc"
                                    placeholder="Select One"
                                    optionValue="stateCode"
                                >
                                </Dropdown>
                                {errors.stateCode && <small className="p-error">{errors.stateCode}</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Postcode</label>
                                <Dropdown
                                    id="postcode"
                                    value={formData.postcode}
                                    onChange={handleDropdownChange}
                                    options={dropdownPostcodeItems}
                                    optionLabel="postcode"
                                    placeholder="Select One"
                                    optionValue="postcode"
                                >
                                </Dropdown>
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>District</label>
                                <Dropdown
                                    id="districtCode"
                                    value={formData.districtCode}
                                    onChange={handleDropdownChange}
                                    options={dropdownDistrictItems}
                                    optionLabel="fullDesc"
                                    placeholder="Select One"
                                    optionValue="districtCode"
                                >
                                </Dropdown>
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>City</label>
                                <InputText
                                    id="city"
                                    type="text"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                />
                            </div>


                            <div className="field col-12 md:col-6">
                                <label>Telephone No.</label>
                                <InputText
                                    id="telephoneNo"
                                    type="text"
                                    value={formData.telephoneNo}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Fax No.</label>
                                <InputText
                                    id="faxNo"
                                    type="text"
                                    value={formData.faxNo}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div className="field col-12 md:col-6">
                                <label>Email</label>
                                <InputText
                                    id="email"
                                    type="text"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                />
                            </div>
                            {deptData && (
                                <>
                                    <div className="field col-12 md:col-6">
                                        <label>Record Created By</label>
                                        <div>{deptData.insertByHasOne.fullName} on {formatDate(formData.insertDate)}</div>
                                    </div>
                                    <div className="field col-12 md:col-6">
                                        <label>Record Updated By</label>
                                        <div>{deptData.modifiedByHasOne.fullName} on {formatDate(formData.modifiedDate)}</div>
                                    </div>
                                </>
                            )

                            }

                        </div>
                    </Fieldset>
                    <div>
                        <br />
                    </div>
                    <Toolbar end={toolbarTemplate}></Toolbar>
                </div>
            </div>
        </div>
    );
};

export default DeptEdit;